import { isNotNullable, isString } from '@app/typeGuards'

import storageService from '@app/services/storageService'

import { getMappedArray } from '@app/utils/arrays'
import { isEmptyString, isNullable } from '@app/utils'
import { convertDropdownItemsToData, findSelectedDropdownMenuItems } from '@app/components/DropdownMenu/utils'

import type { ColumnTitle } from 'antd/es/table/interface'
import type { ReactElement } from 'react'
import type { Column, ColumnGroup } from '@app/components/base/Table/types'
import type { ColumnSearchToolTitleProps } from '@app/hooks/useTableSearch/hooks/useTableSearchTool/components/ColumnSearchToolTitle/types'
import type { DropdownData, DropdownMenuItem } from '@app/types/dropdown'

export const getDropdownItemsFromStorage = (key: string): DropdownMenuItem[] | null => {
  const itemsFromStorageString = storageService.getItem(key)
  const hasItemsFromStorage = !isNullable(itemsFromStorageString) && !isEmptyString(itemsFromStorageString)

  return hasItemsFromStorage ? (JSON.parse(itemsFromStorageString) as DropdownMenuItem[]) : null
}

export const getInitialModifiedColumns = <ColumnType>(
  key: string,
  columns: Array<Column<ColumnType>>
): Array<Column<ColumnType>> => {
  const items = getDropdownItemsFromStorage(key)

  if (isNullable(items)) return columns

  const data = convertDropdownItemsToData(findSelectedDropdownMenuItems(items))

  const columnsMap = getMappedArray(columns, 'key')

  return getModifiedColumnsByDropdownItems(columnsMap, data)
}

export const getModifiedColumnsByDropdownItems = <ColumnType>(
  columnsMap: Record<string, Column<ColumnType>>,
  data: DropdownData[]
): Array<Column<ColumnType>> => {
  const columns = Object.values(columnsMap)

  return columns
    .filter((_, index) => isNotNullable(data[index]?.value))
    .map((_, index) => {
      const item = data[index]
      return columnsMap[item.value]
    })
}

const getLabel = (columnTitle: ColumnTitle<unknown>): string | undefined => {
  if (isString(columnTitle)) return columnTitle

  return (columnTitle as ReactElement<ColumnSearchToolTitleProps>)?.props.title
}

export const convertColumnsToDropdownItems = (columns: Column[]): DropdownMenuItem[] => {
  return columns
    .filter(column => isNotNullable(column.key))
    .map(column => {
      const { key, title } = column

      const item: DropdownMenuItem = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        id: key!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        value: key!,
        label: getLabel(title),
        isSelect: true,
        isDisable: isNotNullable((column as ColumnGroup)?.children)
      }

      return item
    })
}
